import React, { useEffect } from 'react'
import { usePaginatedList } from '../../hooks/usePaginatedList'
import { StudioService } from '../../apilibrary'
import Box from '../../components/box'
import Button from '../../components/button'
import useCurrentUserId from '../../hooks/useCurrentUserId'
import Link from '../../components/router/link'
import Grid from '../../components/grid'
import SpaceItem from './space_item'
import { PopupNotificationType, useShowNotification } from '../../contexts/popup_notification'
import Typography from '../../components/typography'
import { IPagination } from '../../apilibrary/types/common'
import { StudioStatus } from '../../apilibrary/types/studio'
import { trackStudioDeletion } from '../../utils/analytics'

interface StudioCardInfo {
  id: string
  status: StudioStatus
  title: string
  coverPictureUrl: string
  rate: number
  maxOccupancy: number
}

interface SpacesFragmentProps {
  userId: string
}

const GetFetchFunction = (userId: string) => async (pagination: IPagination) => {
  const studios = await StudioService.getStudiosForStudioOwner(userId, pagination)
  const studiosWithCoverPicture = await Promise.all(studios.items.map(async (studio) => {
    const coverPictureUrl = await studio.info.studioPictures[0].getPublicUrl()
    return {
      id: studio.id,
      status: studio.status,
      title: studio.info.title,
      coverPictureUrl,
      rate: studio.info.rate,
      maxOccupancy: studio.info.maxOccupancy
    }
  }))
  return {
    items: studiosWithCoverPicture,
    nextToken: studios.nextToken
  }
}

const SpacesFragment: React.FC<SpacesFragmentProps> = ({
  userId
}) => {
  const loggedInUserId = useCurrentUserId()
  const showNotification = useShowNotification();

  const {
    items: studios,
    hasMore,
    fetchNextPage,
    setItems: setStudios,
    reset: resetStudios
  } = usePaginatedList<StudioCardInfo>({
    fetchFunction: GetFetchFunction(userId)
  })

  const handleDelistStudio = async (studioId: string): Promise<void> => {
    // TODO: Handle deleting studio from API and removing from studio list on render
    try {
      await StudioService.delistStudio(studioId)
      const newStudios = studios.map((studio) => {
        if (studio.id === studioId) {
          return {
            ...studio,
            status: StudioStatus.DELISTED
          }
        }
        return studio
      })
      await trackStudioDeletion(studioId, loggedInUserId);
      setStudios(newStudios)
    } catch (e) {
      console.error(e)
      let message = 'Something went wrong. Please try again later.'
      if (e instanceof Error && e.message) {
        message = e.message
      }
      showNotification({
        message,
        type: PopupNotificationType.ERROR
      })
      throw e
    }
  }

  const handleEnlistStudio = async (studioId: string): Promise<void> => {
    try {
      await StudioService.enlistStudio(studioId)

      const newStudios = studios.map((studio) => {
        if (studio.id === studioId) {
          return {
            ...studio,
            status: StudioStatus.ACTIVE
          }
        }
        return studio
      }
      )
      setStudios(newStudios)
    } catch (e) {
      console.error(e)
      let message = 'Something went wrong. Please try again later.'
      if (e instanceof Error && e.message) {
        message = e.message
      }
      showNotification({
        message,
        type: PopupNotificationType.ERROR
      })
      throw e
    }
  }

  useEffect(() => {
    console.log('user id changed, resetting studios')
    void resetStudios({
      fetchFunction: GetFetchFunction(userId)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  return (
    <Box>
      {loggedInUserId === userId && (
        <Link removeTextDecoration to='/studio-listings/add'>
          <Button
            color='primary'
            sx={{
              display: 'block',
              ml: 'auto'
            }}
            variant='outlined'
          >
            + Add your space
          </Button>
        </Link>
      )}
      <Grid mt={2} container spacing={2}>
        {studios.map((studio) => (
          <Grid key={studio.id} item xs={12} sm={6} md={4}>
            <SpaceItem
              id={studio.id}
              status={studio.status}
              title={studio.title}
              coverPictureUrl={studio.coverPictureUrl}
              rate={studio.rate}
              maxOccupancy={studio.maxOccupancy}
              editActions={{
                allowEdit: loggedInUserId === userId,
                handleEnlist: async () => { await handleEnlistStudio(studio.id); },
                handleDelist: async () => { await handleDelistStudio(studio.id); }
              }}
            />
          </Grid>
        ))}
        {studios.length === 0 && (
          <Grid item xs={12}>
            <Box textAlign='center'>
              <Typography variant='body1'>
                {loggedInUserId === userId
                  ? 'You have not added any spaces yet.'
                  : 'This user has not added any spaces yet.'
                }
              </Typography>
            </Box>
          </Grid>
        )}
        {hasMore && (
          <Grid item xs={12}>
            <Box textAlign='center'>
              <Button
                color='primary'
                onClick={fetchNextPage}
                fullWidth
                variant='outlined'
              >
                Load more
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

export default SpacesFragment
